import {
  Component,
  Input,
  Output,
  EventEmitter,
  computed,
  inject,
  ViewChild,
  ElementRef,
  signal,
  OnDestroy,
  ViewContainerRef,
} from '@angular/core';

import { Overlay, OverlayRef, OverlayModule, ConnectedPosition } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { cva } from '../utils/class-variance.util';
import { ThemeSection } from '../services/theme.service';
import { MenuTrigger, MenuPosition } from '../models/menu.model';

const menuVariants = cva({
  base: [
    'min-w-[200px] max-w-[400px]',
    'overflow-hidden border rounded-[24px]',
    'shadow-2xl origin-top-right',
    'transition-all duration-300 ease-out',
    'transform-gpu relative',
    'backdrop-blur-2xl',
    'before:content-[""] before:absolute before:inset-0',
    'before:bg-glass-gradient-light dark:before:bg-glass-gradient-dark',
    'before:opacity-70 before:rounded-[24px] before:-z-10 before:pointer-events-none',
    'after:content-[""] after:absolute after:inset-0',
    'after:rounded-[24px] after:pointer-events-none after:-z-10',
  ].join(' '),
  variants: {
    variant: {
      default: [
        'bg-linear-to-br from-slate-800/94 via-slate-900/95 to-blue-950/90',
        'border border-blue-200/14',
        'shadow-[inset_0_1px_0_rgba(255,255,255,0.04),0_24px_60px_rgba(2,8,23,0.38)]',
        'after:bg-[radial-gradient(circle_at_top_right,rgba(37,99,235,0.12),transparent_34%),linear-gradient(180deg,rgba(255,255,255,0.03)_0%,rgba(148,163,184,0.01)_100%)]',
      ].join(' '),
      elevated: [
        'bg-[radial-gradient(circle_at_top_right,rgba(59,130,246,0.14),transparent_32%),linear-gradient(180deg,rgba(24,40,66,0.96)_0%,rgba(10,18,34,0.98)_100%)]',
        'border border-[rgba(146,176,218,0.2)]',
        'shadow-[inset_0_1px_0_rgba(255,255,255,0.05),0_24px_56px_rgba(2,8,23,0.42),0_0_48px_rgba(37,99,235,0.08)]',
        'after:bg-[linear-gradient(180deg,rgba(255,255,255,0.04)_0%,rgba(59,130,246,0.03)_100%)]',
      ].join(' '),
      premium: [
        'bg-[radial-gradient(circle_at_top_right,rgba(59,130,246,0.18),transparent_30%),radial-gradient(circle_at_bottom_left,rgba(14,165,233,0.08),transparent_40%),linear-gradient(180deg,rgba(24,40,66,0.98)_0%,rgba(10,18,34,1)_100%)]',
        'border border-[rgba(165,195,232,0.24)]',
        'shadow-[inset_0_1px_0_rgba(255,255,255,0.06),0_28px_64px_rgba(2,8,23,0.46),0_0_56px_rgba(37,99,235,0.1)]',
        'after:bg-[linear-gradient(180deg,rgba(255,255,255,0.05)_0%,rgba(59,130,246,0.04)_100%)]',
      ].join(' '),
    },
    size: {
      sm: 'min-w-[160px] py-1',
      md: 'min-w-[200px] py-2',
      lg: 'min-w-[280px] py-3',
      xl: 'min-w-[320px] py-4',
    },
    state: {
      closed: 'opacity-0 scale-95 pointer-events-none -translate-y-2',
      open: 'opacity-100 scale-100 pointer-events-auto translate-y-0',
    },
  },
  defaultVariants: {
    variant: 'default',
    size: 'md',
    state: 'closed',
  },
});

const menuItemVariants = cva({
  base: [
    'relative flex items-center gap-3 px-4 py-3 text-sm cursor-pointer rounded-[18px] mx-2',
    'transition-all duration-200 ease-out',
    'group',
    'hover:bg-white/7 focus:bg-white/7',
    'focus:outline-none',
    'hover:border-blue-200/12 focus:border-blue-200/12',
    'border border-transparent',
    'hover:shadow-[inset_0_1px_0_rgba(255,255,255,0.04)]',
    'active:scale-[0.99]',
  ].join(' '),
  variants: {
    variant: {
      default: 'text-slate-100 hover:text-white',
      primary: 'text-blue-200 hover:text-blue-100',
      danger: 'text-red-200 hover:text-red-100',
      success: 'text-emerald-200 hover:text-emerald-100',
    },
    disabled: {
      true: 'opacity-50 cursor-not-allowed pointer-events-none',
      false: '',
    },
  },
  defaultVariants: {
    variant: 'default',
    disabled: 'false',
  },
});

import { MenuItem } from '../models/menu.model';

export { MenuItem } from '../models/menu.model';

@Component({
  selector: 'ui-menu-glass',
  standalone: true,
  imports: [OverlayModule],
  templateUrl: './menu-glass.component.html',
  host: {
    class: 'relative inline-block',
  },
})
export class MenuGlassComponent implements OnDestroy {
  private overlay = inject(Overlay);
  private viewContainerRef = inject(ViewContainerRef);

  @ViewChild('triggerRef') triggerRef!: ElementRef;
  @ViewChild('menuTemplate') menuTemplate!: import('@angular/core').TemplateRef<unknown>;

  @Input() items: MenuItem[] = [];
  @Input() trigger: MenuTrigger = 'click';
  @Input() position: MenuPosition = 'bottom-start';
  @Input() variant: 'default' | 'elevated' | 'premium' = 'default';
  @Input() size: 'sm' | 'md' | 'lg' | 'xl' = 'md';
  @Input() triggerClass: string = '';
  @Input() showBackdrop: boolean = false;
  @Input() themeSection: ThemeSection = 'global';
  @Input() hasCustomContent: boolean = false; // Set to true when using slot=content

  @Output() itemClick = new EventEmitter<MenuItem>();
  @Output() openChange = new EventEmitter<boolean>();

  protected isOpen = signal(false);
  protected closeTimer: ReturnType<typeof setTimeout> | null = null;
  private overlayRef: OverlayRef | null = null;

  protected computedMenuClasses = computed(() => {
    return menuVariants({
      variant: this.variant,
      size: this.size,
      state: this.isOpen() ? 'open' : 'closed',
    });
  });

  protected getItemClasses(item: MenuItem): string {
    return menuItemVariants({
      variant: item.variant || 'default',
      disabled: item.disabled ? 'true' : 'false',
    });
  }

  open(): void {
    if (this.overlayRef) return;

    this.clearCloseTimer();

    // Determine overlay positions based on menu position
    const positions = this.getOverlayPositions();

    const positionStrategy = this.overlay
      .position()
      .flexibleConnectedTo(this.triggerRef)
      .withPositions(positions)
      .withPush(false);

    this.overlayRef = this.overlay.create({
      positionStrategy,
      scrollStrategy: this.overlay.scrollStrategies.reposition(),
      hasBackdrop: true,
      backdropClass: 'cdk-overlay-transparent-backdrop',
      panelClass: 'ui-menu-overlay-pane',
    });

    const portal = new TemplatePortal(this.menuTemplate, this.viewContainerRef);
    this.overlayRef.attach(portal);

    // Close when clicking outside
    this.overlayRef.backdropClick().subscribe(() => {
      this.close();
    });

    this.isOpen.set(true);
    this.openChange.emit(true);
  }

  close(): void {
    if (this.overlayRef) {
      this.overlayRef.dispose();
      this.overlayRef = null;
    }
    this.isOpen.set(false);
    this.openChange.emit(false);
  }

  toggle(): void {
    if (this.isOpen()) {
      this.close();
    } else {
      this.open();
    }
  }

  protected startCloseTimer(): void {
    this.closeTimer = setTimeout(() => this.close(), 150);
  }

  protected clearCloseTimer(): void {
    if (this.closeTimer) {
      clearTimeout(this.closeTimer);
      this.closeTimer = null;
    }
  }

  protected handleItemClick(item: MenuItem, event: Event): void {
    if (item.disabled) return;

    event.stopPropagation();

    if (item.action) {
      item.action();
    }

    this.itemClick.emit(item);

    // Close menu after item click (unless it has submenu)
    if (!item.submenu || item.submenu.length === 0) {
      this.close();
    }
  }

  protected handleMenuClick(event: Event): void {
    event.stopPropagation();
  }

  private getOverlayPositions(): ConnectedPosition[] {
    const offsetY = 4;
    const offsetX = 4;

    switch (this.position) {
      case 'bottom':
        return [
          { originX: 'center', originY: 'bottom', overlayX: 'center', overlayY: 'top', offsetY },
          { originX: 'center', originY: 'top', overlayX: 'center', overlayY: 'bottom', offsetY: -offsetY },
        ];
      case 'bottom-start':
        return [
          { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY },
          { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom', offsetY: -offsetY },
        ];
      case 'bottom-end':
        return [
          { originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top', offsetY },
          { originX: 'end', originY: 'top', overlayX: 'end', overlayY: 'bottom', offsetY: -offsetY },
        ];
      case 'top':
        return [
          { originX: 'center', originY: 'top', overlayX: 'center', overlayY: 'bottom', offsetY: -offsetY },
          { originX: 'center', originY: 'bottom', overlayX: 'center', overlayY: 'top', offsetY },
        ];
      case 'top-start':
        return [
          { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom', offsetY: -offsetY },
          { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY },
        ];
      case 'top-end':
        return [
          { originX: 'end', originY: 'top', overlayX: 'end', overlayY: 'bottom', offsetY: -offsetY },
          { originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top', offsetY },
        ];
      case 'right-start':
        return [
          { originX: 'end', originY: 'top', overlayX: 'start', overlayY: 'top', offsetX },
          { originX: 'start', originY: 'top', overlayX: 'end', overlayY: 'top', offsetX: -offsetX },
        ];
      case 'left-start':
        return [
          { originX: 'start', originY: 'top', overlayX: 'end', overlayY: 'top', offsetX: -offsetX },
          { originX: 'end', originY: 'top', overlayX: 'start', overlayY: 'top', offsetX },
        ];
      default:
        return [{ originX: 'center', originY: 'bottom', overlayX: 'center', overlayY: 'top', offsetY }];
    }
  }

  ngOnDestroy(): void {
    if (this.overlayRef) {
      this.overlayRef.dispose();
    }
    this.clearCloseTimer();
    this.closeTimer = null;
  }
}

/**
 * Usage Examples:
 *
 * Basic dropdown menu:
 * <ui-menu-glass [items]="menuItems">
 *   <button slot="trigger" class="...">Menu</button>
 * </ui-menu-glass>
 *
 * With icons:
 * <ui-menu-glass [items]="menuItems">
 *   <button slot="trigger">Actions</button>
 *   <ui-icon slot="icon-edit" name="edit" size="sm"></ui-icon>
 *   <ui-icon slot="icon-delete" name="trash" size="sm"></ui-icon>
 * </ui-menu-glass>
 *
 * Premium e-commerce variant:
 * <ui-menu-glass variant="premium" size="lg" [items]="accountMenuItems">
 *   <div slot="trigger" class="cursor-pointer">
 *     <img src="avatar.jpg" class="w-8 h-8 rounded-full">
 *   </div>
 * </ui-menu-glass>
 *
 * Component setup:
 * menuItems: MenuItem[] = [
 *   { label: 'Edit Product', icon: 'edit', action: () => this.editProduct() },
 *   { label: 'View Details', icon: 'eye' },
 *   { divider: true },
 *   { label: 'Delete', icon: 'delete', variant: 'danger', action: () => this.deleteProduct() }
 * ];
 */
